import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AdminMessageList, AdminMessageEditor, AdminMessageAnalytics } from '../components/Admin/Messages';
import WritersPanel from '../components/Admin/Crm/WritersPanel';
import LeadsPanel from '../components/Admin/Crm/LeadsPanel';
import './AdminPage.css';

/**
 * Admin dashboard page with tabbed navigation.
 *
 * Tabs:
 * - Messages: User engagement messages
 *
 * Protected by AdminRoute - only accessible to admin users.
 */

const TABS = [
  { key: 'writers', label: 'Writers' },
  { key: 'leads', label: 'Leads' },
  { key: 'messages', label: 'Messages' },
];

function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.key === requested) ? requested : 'writers';

  // Messages tab state
  const [messagesView, setMessagesView] = useState('list'); // 'list', 'edit', 'analytics'
  const [selectedMessage, setSelectedMessage] = useState(null);

  const handleEditMessage = (message) => {
    setSelectedMessage(message);
    setMessagesView('edit');
  };

  const handleAnalytics = (message) => {
    setSelectedMessage(message);
    setMessagesView('analytics');
  };

  const handleCreateMessage = () => {
    setSelectedMessage(null);
    setMessagesView('edit');
  };

  const handleSaveMessage = () => {
    setSelectedMessage(null);
    setMessagesView('list');
  };

  const handleCancelEdit = () => {
    setSelectedMessage(null);
    setMessagesView('list');
  };

  const handleBackFromAnalytics = () => {
    setSelectedMessage(null);
    setMessagesView('list');
  };

  const renderMessagesContent = () => {
    switch (messagesView) {
      case 'edit':
        return (
          <AdminMessageEditor
            message={selectedMessage}
            onSave={handleSaveMessage}
            onCancel={handleCancelEdit}
          />
        );
      case 'analytics':
        return (
          <AdminMessageAnalytics
            message={selectedMessage}
            onBack={handleBackFromAnalytics}
          />
        );
      default:
        return (
          <AdminMessageList
            onEdit={handleEditMessage}
            onAnalytics={handleAnalytics}
            onCreate={handleCreateMessage}
          />
        );
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Admin</h1>
        <p className="admin-page__subtitle">Writers, people waiting to join, and messages.</p>
        <Link className="admin-page__back" to="/">Back to Brainstormy</Link>
      </div>

      <div className="admin-page__tabs" role="tablist" aria-label="Admin sections">
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`admin-tab-${tab.key}`}
            aria-selected={activeTab === tab.key}
            className={`admin-tab ${activeTab === tab.key ? 'admin-tab--active' : ''}`}
            onClick={() => {
              setSearchParams({ tab: tab.key });
              if (tab.key === 'messages') {
                setMessagesView('list');
                setSelectedMessage(null);
              }
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div
        className="admin-page__content"
        role="tabpanel"
        aria-labelledby={`admin-tab-${activeTab}`}
      >
        {activeTab === 'writers' && <WritersPanel />}
        {activeTab === 'leads' && <LeadsPanel />}
        {activeTab === 'messages' && renderMessagesContent()}
      </div>
    </div>
  );
}

export default AdminPage;
